/**
 * FirmaBilgiKartlari.jsx — "Firma Bilgileri" sekmesinin firma-tarafı ve denetmen-tarafı
 * sayfalarca PAYLAŞILAN blokları: katalog bilgileri, fotoğraf galerisi, harita konumu ve
 * firma logosu. Backlog #60 (backend #61).
 *
 * Neden ortak: aynı üç blok hem `pages/Firma/FirmaKartPage.jsx`te (firmanın kendi portalı)
 * hem `pages/Management/Modules/Firmalar/FirmaProfilIncelemePage.jsx`te (denetmen/admin,
 * ayrıca FirmaDetayPage'in ilk sekmesi olarak gömülü) görünüyor ve ikisi de aynı
 * `profile-page` cevabını okuyor — `profile` alt-nesnesinin şekli birebir aynı, bu yüzden
 * bloklar sunum bileşeni olarak paylaşılabiliyor. Veri ÇEKME paylaşılmıyor: portal
 * firmaProfileService (mock katmanlı), denetmen tarafı firmaService (canlı) kullanır.
 *
 * İki sayfanın kart çerçevesi farklı (portalda kırmızı şerit etiketli InfoRibbonCard,
 * denetmen tarafında düz CardHeader) — bu yüzden bloklar `variant` alır ve her sayfa
 * kendi görsel dilinde kalır. Şerit ve InfoField eskiden FirmaKartPage'e dosya-yereldi.
 *
 * YETKİ: fotoğraf/profil YAZMA uçları kendi firman ya da personel (staff) isen açıktır —
 * bir denetmen herhangi bir firmanın galerisini ve konumunu düzenleyebilir. OKUMA tamamen
 * serbesttir. Ayrıntı: util/knowledgebase/firma-foto-konum-katalog.md.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Badge, Spinner, Alert, Input, Label,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Pencil, MapPin, Upload, Trash2, Star, Image as ImageIcon, X, Plus } from "lucide-react";
import FirmaKonumHarita from "./FirmaKonumHarita";
import {
  firmaFileSrc, fotoDosyaHatasi, uploadFirmaPhoto, updateFirmaPhoto, setFirmaPhotoCover,
  deleteFirmaPhoto, saveFirmaProfile, koordinatHatasi, kategoriHatasi, kategoriNormalize,
  kategoriAyniMi, apiErrorMessage, FOTO_MAX_ADET,
  getFuarFirmaKatalog, saveFuarFirmaKatalog,
} from "../../services/firmaMediaService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// ─── Kart çerçeveleri ─────────────────────────────────────────────────────────

/** Portal görünümü: sol üstte kırmızı şerit etiket, sağ üstte aksiyonlar. */
export function InfoRibbonCard({ label, actions, children, tour }) {
  return (
    <Card className="position-relative overflow-hidden mb-4" data-tour={tour}>
      <span
        className="badge bg-danger position-absolute text-white"
        style={{
          top: 12, left: -6, padding: "4px 16px 4px 10px",
          clipPath: "polygon(0 0, 100% 0, 88% 50%, 100% 100%, 0 100%)",
          fontSize: "0.7rem", zIndex: 2,
        }}
      >
        {label}
      </span>
      {actions && (
        <div className="position-absolute d-flex align-items-center gap-2" style={{ top: 10, right: 12, zIndex: 2 }}>
          {actions}
        </div>
      )}
      <CardBody className="pt-5">{children}</CardBody>
    </Card>
  );
}

export function InfoField({ label, value }) {
  return (
    <div className="mb-2 small">
      <span className="text-muted">{label}:</span> <span className="fw-semibold">{value ?? "—"}</span>
    </div>
  );
}

/**
 * Sayfanın kendi görsel diline göre çerçeve seçer.
 * @param {"ribbon"|"header"} variant  ribbon → portal, header → denetmen tarafı
 */
export function FirmaBilgiCard({ variant = "header", label, actions, children, className = "mb-3", tour }) {
  if (variant === "ribbon") {
    return <InfoRibbonCard label={label} actions={actions} tour={tour}>{children}</InfoRibbonCard>;
  }
  return (
    <Card className={className} data-tour={tour}>
      <CardHeader className="bg-transparent d-flex align-items-center justify-content-between">
        <strong>{label}</strong>
        {actions}
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

/** Düzenle butonu — portalda kırmızı, denetmen tarafında sarı (sayfaların mevcut dili). */
function DuzenleButton({ variant, onClick, title, className, children = i18n.t("common:duzenle") }) {
  const props = variant === "ribbon" ? { color: "danger" } : { color: "warning", outline: true };
  return (
    <Button {...props} size="sm" title={title} className={className} onClick={onClick}>
      <Pencil size={14} className="me-1" /> {children}
    </Button>
  );
}

// ─── Firma logosu ─────────────────────────────────────────────────────────────

/**
 * `firmaLogoUrl` + `firmaLogoSource` (#61) üzerinden logo. Ham `firmaLogo` sütunu tek
 * başına dosyanın nerede durduğunu söylemiyordu — 28.972 firmanın 152'si eski PHP
 * sunucusunda MD5 adlı bir dosya taşıyor — bu yüzden her zaman URL alanı okunur.
 * Logo yoksa (source NONE) baş harfler basılır; backend varsayılan görsel vermiyor.
 */
export function FirmaLogo({ profile, size = 56 }) {
  const { t } = useTranslation("components");
  const [hata, setHata] = useState(false);
  const src = !hata ? firmaFileSrc(profile?.firmaLogoUrl) : null;
  const basHarf = useMemo(() => {
    const ad = String(profile?.firmaShortName || profile?.firmaName || "").trim();
    if (!ad) return "?";
    const kelimeler = ad.split(/[\s.]+/).filter(Boolean);
    const iki = kelimeler.length > 1 ? kelimeler[0][0] + kelimeler[1][0] : ad.slice(0, 2);
    return iki.toLocaleUpperCase("tr-TR");
  }, [profile]);

  if (src) {
    return (
      <img
        src={src}
        alt={profile?.firmaName || t("firmaBilgi.firmaLogosu")}
        onError={() => setHata(true)}
        style={{ width: size, height: size, objectFit: "contain" }}
        className="rounded border bg-white p-1 flex-shrink-0"
      />
    );
  }
  return (
    <div
      className="rounded border bg-light d-flex align-items-center justify-content-center fw-semibold text-muted flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.32 }}
    >
      {basHarf}
    </div>
  );
}

// ─── Katalog bilgileri ────────────────────────────────────────────────────────

/**
 * Kategoriler (serbest metin) + katalog açıklaması.
 * @param {Function} onSaved  kaydettikten sonra çağrılır; sayfa profili tazeler.
 */
// #528: `label` / `modalBaslik` — the company portal's Firma Kartı names this card "Areas of Activities and Services" (#539; WAS "Business Activities"); the default stays "Catalogue Information"
// (staff pages). WAS: no props, always t("firmaBilgi.katalogBilgileri") / t("firmaBilgi.katalogBilgileriniDuzenle").
export function KatalogBilgileriCard({ firmaId, profile, variant = "header", canEdit = false, onSaved, label, modalBaslik, tour }) {
  const { t } = useTranslation("components");
  const [modalAcik, setModalAcik] = useState(false);
  const kategoriler = profile?.kategoriler || [];

  return (
    <>
      <FirmaBilgiCard
        variant={variant}
        label={label ?? t("firmaBilgi.katalogBilgileri")}
        actions={canEdit ? <DuzenleButton variant={variant} onClick={() => setModalAcik(true)} /> : null}
        tour={tour}
      >
        <Row>
          <Col md={4}>
            {kategoriler.length === 0 ? (
              <div className="text-muted small">{t("firmaBilgi.kategoriGirilmemis")}</div>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                {kategoriler.map((k) => (
                  <Badge key={k} color="light" className="text-body border fw-normal">{k}</Badge>
                ))}
              </div>
            )}
          </Col>
          <Col md={8}>
            {profile?.katalogAciklama ? (
              <p className="text-muted small mb-0" style={{ whiteSpace: "pre-wrap" }}>{profile.katalogAciklama}</p>
            ) : (
              <div className="text-muted small">{t("firmaBilgi.katalogAciklamasiGirilmemis")}</div>
            )}
          </Col>
        </Row>
      </FirmaBilgiCard>

      {/* Modal yalnızca açıkken mount edilir — form başlangıç değerlerini prop'tan
          useState initializer'ıyla alır, senkronizasyon effect'i gerekmez (bu repoda
          react-hooks/set-state-in-effect hata seviyesinde). */}
      {modalAcik && (
        <KatalogDuzenleModal
          toggle={() => setModalAcik(false)}
          firmaId={firmaId}
          profile={profile}
          onSaved={onSaved}
          baslik={modalBaslik}
        />
      )}
    </>
  );
}

/**
 * Kategoriler serbest metindir: yazılan ad yoksa yeni bir kayıt açar, varsa tekrar kullanılır.
 * Global `GET /firma/industryTypes` listesi BİLEREK öneri kaynağı olarak kullanılmıyor —
 * o liste bütün firmaların kategorilerini içerir, öneri olarak göstermek bir firmanın
 * kutusunda başka firmaların kategori adlarını gösterirdi (#61: firmalar yalnızca kendi
 * kategorilerini görür).
 */
function KatalogDuzenleModal({ toggle, firmaId, profile, onSaved, baslik }) {
  const { t } = useTranslation("components");
  const [kategoriler, setKategoriler] = useState(() => profile?.kategoriler || []);
  const [aciklama, setAciklama] = useState(() => profile?.katalogAciklama || "");
  const [yeni, setYeni] = useState("");
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  function ekle() {
    const ad = kategoriNormalize(yeni);
    if (!ad) return;
    if (kategoriler.some((k) => kategoriAyniMi(k, ad))) { setYeni(""); return; }
    const sonraki = [...kategoriler, ad];
    const kh = kategoriHatasi(sonraki);
    if (kh) { setHata(kh); return; }
    setKategoriler(sonraki);
    setYeni("");
    setHata(null);
  }

  async function kaydet() {
    setKaydediliyor(true);
    setHata(null);
    try {
      // Sadece bu iki alan gönderilir; PATCH semantiği sayesinde adres/telefon/e-posta
      // ve konum saklı kalır (#61 öncesi bu çağrı onları null'lardı).
      await saveFirmaProfile(firmaId, { kategoriler, katalogAciklama: aciklama });
      toggle();
      onSaved?.();
    } catch (e) {
      setHata(apiErrorMessage(e, t("firmaBilgi.katalogBilgileriKaydedilemedi")));
    } finally {
      setKaydediliyor(false);
    }
  }

  return (
    <Modal isOpen toggle={toggle}>
      <ModalHeader toggle={toggle}>{baslik ?? t("firmaBilgi.katalogBilgileriniDuzenle")}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        <Label className="small">{t("firmaBilgi.kategoriler")}</Label>
        <div className="d-flex gap-2 mb-2">
          <Input
            bsSize="sm" value={yeni} placeholder={t("firmaBilgi.kategoriYazipEkleyin")}
            onChange={(e) => setYeni(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ekle(); } }}
          />
          <Button color="secondary" outline size="sm" onClick={ekle} disabled={!yeni.trim()}>
            <Plus size={14} />
          </Button>
        </div>
        {kategoriler.length === 0 ? (
          <div className="text-muted small mb-3">{t("firmaBilgi.henuzKategoriEklenmedi")}</div>
        ) : (
          <div className="d-flex flex-wrap gap-1 mb-3">
            {kategoriler.map((k) => (
              <Badge key={k} color="light" className="text-body border fw-normal d-flex align-items-center gap-1">
                {k}
                <span
                  role="button" title={t("firmaBilgi.kaldir")} style={{ cursor: "pointer" }}
                  onClick={() => setKategoriler((list) => list.filter((x) => x !== k))}
                >
                  <X size={12} />
                </span>
              </Badge>
            ))}
          </div>
        )}
        <Label className="small">{t("firmaBilgi.katalogAciklamasi")}</Label>
        <Input
          type="textarea" bsSize="sm" rows={5} value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={kaydediliyor}>{t("common:iptal")}</Button>
        <Button color="primary" onClick={kaydet} disabled={kaydediliyor}>
          {kaydediliyor ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Fuar Katalog Bilgileri (backlog #103/#104) ────────────────────────────────
// KatalogBilgileriCard'ın FUAR-BAZLI eşi — global katalog fuardan bağımsızdır ve
// FirmaBilgiKartlari'nin geri kalanı gibi zaten çekilmiş `profile` prop'unu okur; bu ise
// kendi ayrı GET'ini yapar (firma_id+fuar_id anahtarlı, tamamen farklı bir tablo — global
// katalog HİÇ dokunulmadı, #103'ün kendi notu). Bu yüzden `profile` değil `firmaId`+`fuarId`
// alır ve kendi yükleme durumunu taşır.
export function FuarKatalogBilgileriCard({ firmaId, fuarId, variant = "header", canEdit = false }) {
  const { t } = useTranslation("components");
  const [state, setState] = useState({ loading: true, katalog: null, industries: [] });
  const [modalAcik, setModalAcik] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!firmaId || !fuarId) return undefined;
    let ignore = false;
    getFuarFirmaKatalog(firmaId, fuarId)
      .then((d) => { if (!ignore) setState({ loading: false, katalog: d?.katalog || null, industries: d?.industries || [] }); })
      .catch(() => { if (!ignore) setState({ loading: false, katalog: null, industries: [] }); });
    return () => { ignore = true; };
  }, [firmaId, fuarId, reloadToken]);

  const kategoriler = useMemo(
    () => state.industries.map((i) => i.industry_type_name).filter(Boolean),
    [state.industries]
  );
  const aciklama = state.katalog?.aciklama || "";

  if (!fuarId) {
    return (
      <FirmaBilgiCard variant={variant} label={t("firmaBilgi.fuarKatalogBilgileri")}>
        <div className="text-muted small">{t("firmaBilgi.buBilgiyiGormekDuzenlemekIcin")}</div>
      </FirmaBilgiCard>
    );
  }

  return (
    <>
      <FirmaBilgiCard
        variant={variant}
        label={t("firmaBilgi.fuarKatalogBilgileri")}
        actions={canEdit ? <DuzenleButton variant={variant} onClick={() => setModalAcik(true)} /> : null}
      >
        {/* #388: without a company (still loading, or staff with none) nothing is asked, so it must not spin forever. WAS: state.loading ? ( */}
        {state.loading && firmaId ? (
          <div className="text-center py-2"><Spinner size="sm" color="primary" /></div>
        ) : (
          <Row>
            <Col md={4}>
              {kategoriler.length === 0 ? (
                <div className="text-muted small">{t("firmaBilgi.buFuarIcinKategoriGirilmemis")}</div>
              ) : (
                <div className="d-flex flex-wrap gap-1">
                  {kategoriler.map((k) => (
                    <Badge key={k} color="light" className="text-body border fw-normal">{k}</Badge>
                  ))}
                </div>
              )}
            </Col>
            <Col md={8}>
              {aciklama ? (
                <p className="text-muted small mb-0" style={{ whiteSpace: "pre-wrap" }}>{aciklama}</p>
              ) : (
                <div className="text-muted small">{t("firmaBilgi.buFuarIcinKatalogAciklamasi")}</div>
              )}
            </Col>
          </Row>
        )}
      </FirmaBilgiCard>

      {modalAcik && (
        <FuarKatalogDuzenleModal
          toggle={() => setModalAcik(false)}
          firmaId={firmaId}
          fuarId={fuarId}
          kategoriler={kategoriler}
          aciklama={aciklama}
          onSaved={() => setReloadToken((n) => n + 1)}
        />
      )}
    </>
  );
}

function FuarKatalogDuzenleModal({ toggle, firmaId, fuarId, kategoriler: baslangicKategoriler, aciklama: baslangicAciklama, onSaved }) {
  const { t } = useTranslation("components");
  const [kategoriler, setKategoriler] = useState(() => baslangicKategoriler || []);
  const [aciklama, setAciklama] = useState(() => baslangicAciklama || "");
  const [yeni, setYeni] = useState("");
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  function ekle() {
    const ad = kategoriNormalize(yeni);
    if (!ad) return;
    if (kategoriler.some((k) => kategoriAyniMi(k, ad))) { setYeni(""); return; }
    const sonraki = [...kategoriler, ad];
    const kh = kategoriHatasi(sonraki);
    if (kh) { setHata(kh); return; }
    setKategoriler(sonraki);
    setYeni("");
    setHata(null);
  }

  async function kaydet() {
    setKaydediliyor(true);
    setHata(null);
    try {
      await saveFuarFirmaKatalog(firmaId, fuarId, { kategoriler, aciklama });
      toggle();
      onSaved?.();
    } catch (e) {
      setHata(apiErrorMessage(e, t("firmaBilgi.fuarKatalogBilgileriKaydedilemedi")));
    } finally {
      setKaydediliyor(false);
    }
  }

  return (
    <Modal isOpen toggle={toggle}>
      <ModalHeader toggle={toggle}>{t("firmaBilgi.fuarKatalogBilgileriniDuzenle")}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        <div className="text-muted small mb-3">
          {t("firmaBilgi.buBilgilerYalnizcaSeciliFuar")}
        </div>
        <Label className="small">{t("firmaBilgi.kategorilerBuFuarIcin")}</Label>
        <div className="d-flex gap-2 mb-2">
          <Input
            bsSize="sm" value={yeni} placeholder={t("firmaBilgi.kategoriYazipEkleyin")}
            onChange={(e) => setYeni(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ekle(); } }}
          />
          <Button color="secondary" outline size="sm" onClick={ekle} disabled={!yeni.trim()}>
            <Plus size={14} />
          </Button>
        </div>
        {kategoriler.length === 0 ? (
          <div className="text-muted small mb-3">{t("firmaBilgi.henuzKategoriEklenmedi")}</div>
        ) : (
          <div className="d-flex flex-wrap gap-1 mb-3">
            {kategoriler.map((k) => (
              <Badge key={k} color="light" className="text-body border fw-normal d-flex align-items-center gap-1">
                {k}
                <span
                  role="button" title={t("firmaBilgi.kaldir")} style={{ cursor: "pointer" }}
                  onClick={() => setKategoriler((list) => list.filter((x) => x !== k))}
                >
                  <X size={12} />
                </span>
              </Badge>
            ))}
          </div>
        )}
        <Label className="small">{t("firmaBilgi.katalogAciklamasiBuFuarIcin")}</Label>
        <Input
          type="textarea" bsSize="sm" rows={5} value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={kaydediliyor}>{t("common:iptal")}</Button>
        <Button color="primary" onClick={kaydet} disabled={kaydediliyor}>
          {kaydediliyor ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Fotoğraflar ──────────────────────────────────────────────────────────────

const BOS_BASLIK_MODAL = { isOpen: false, foto: null, deger: "", kaydediliyor: false };
const BOS_SIL_MODAL = { isOpen: false, foto: null, siliniyor: false };

/**
 * Galeri. `photos` `profile-page`in `fotograflar[]` alanıdır (kapak her zaman ilk sırada);
 * ayrı bir okuma yapılmaz. Her yazma tazelenmiş listeyi döndüğü için `onChanged` ile
 * doğrudan o liste yukarı verilir — sayfa yeniden yüklenmez (#63'teki aynı desen).
 */
export function FirmaFotograflarCard({ firmaId, photos = [], variant = "header", canEdit = false, onChanged, tour }) {
  const { t } = useTranslation("components");
  const dosyaRef = useRef(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [onizleme, setOnizleme] = useState(null);
  const [baslikModal, setBaslikModal] = useState(BOS_BASLIK_MODAL);
  const [silModal, setSilModal] = useState(BOS_SIL_MODAL);
  const [islemdeki, setIslemdeki] = useState(null);

  async function dosyaSecildi(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const dh = fotoDosyaHatasi(file, photos.length);
    if (dh) { setHata(dh); return; }
    setYukleniyor(true);
    setHata(null);
    try {
      onChanged?.(await uploadFirmaPhoto(firmaId, file));
    } catch (err) {
      setHata(apiErrorMessage(err, t("firmaBilgi.fotografYuklenemedi")));
    } finally {
      setYukleniyor(false);
    }
  }

  async function kapakYap(foto) {
    setIslemdeki(foto.id);
    setHata(null);
    try {
      onChanged?.(await setFirmaPhotoCover(firmaId, foto.id));
    } catch (e) {
      setHata(apiErrorMessage(e, t("firmaBilgi.kapakFotografiGuncellenemedi")));
    } finally {
      setIslemdeki(null);
    }
  }

  async function baslikKaydet() {
    setBaslikModal((m) => ({ ...m, kaydediliyor: true }));
    setHata(null);
    try {
      const temiz = baslikModal.deger.trim();
      onChanged?.(await updateFirmaPhoto(firmaId, baslikModal.foto.id, { baslik: temiz || null }));
      setBaslikModal(BOS_BASLIK_MODAL);
    } catch (e) {
      setHata(apiErrorMessage(e, t("firmaBilgi.baslikKaydedilemedi")));
      setBaslikModal(BOS_BASLIK_MODAL);
    }
  }

  async function sil() {
    setSilModal((m) => ({ ...m, siliniyor: true }));
    setHata(null);
    try {
      onChanged?.(await deleteFirmaPhoto(firmaId, silModal.foto.id));
    } catch (e) {
      setHata(apiErrorMessage(e, t("firmaBilgi.fotografKaldirilamadi")));
    } finally {
      setSilModal(BOS_SIL_MODAL);
    }
  }

  const yuklemeAksiyonu = canEdit ? (
    <>
      <input ref={dosyaRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="d-none" onChange={dosyaSecildi} />
      <Button
        color={variant === "ribbon" ? "danger" : "primary"} outline={variant !== "ribbon"} size="sm"
        disabled={yukleniyor || photos.length >= FOTO_MAX_ADET}
        title={photos.length >= FOTO_MAX_ADET ? t("firmaBilgi.enFazlaFotograf", { FOTO_MAX_ADET }) : undefined}
        onClick={() => dosyaRef.current?.click()}
      >
        {yukleniyor ? <Spinner size="sm" /> : <><Upload size={14} className="me-1" />{" "}{t("common:yukle")}</>}
      </Button>
    </>
  ) : null;

  return (
    <>
      <FirmaBilgiCard variant={variant} label={t("firmaBilgi.fotograflar")} actions={yuklemeAksiyonu} tour={tour}>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        {photos.length === 0 ? (
          <div className="text-muted small d-flex align-items-center gap-2">
            <ImageIcon size={16} />{" "}{t("firmaBilgi.henuzFotografYuklenmedi")}
          </div>
        ) : (
          <Row className="g-3">
            {photos.map((f) => (
              <Col xs={6} md={3} key={f.id}>
                <div className="border rounded-3 overflow-hidden h-100 position-relative">
                  <div
                    role="button" title={f.baslik || t("firmaBilgi.buyut")} style={{ cursor: "zoom-in" }}
                    onClick={() => setOnizleme(f)}
                  >
                    <img
                      src={firmaFileSrc(f.url)} alt={f.baslik || t("firmaBilgi.firmaFotografi")}
                      style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                    />
                  </div>
                  {f.kapak && (
                    <Badge color="danger" className="position-absolute" style={{ top: 6, left: 6 }}>{t("firmaBilgi.kapak")}</Badge>
                  )}
                  <div className="px-2 py-1 d-flex align-items-center justify-content-between gap-1">
                    <span className="small text-truncate" title={f.baslik || ""}>{f.baslik || "—"}</span>
                    {canEdit && (
                      <span className="d-flex align-items-center gap-1 flex-shrink-0">
                        {islemdeki === f.id ? <Spinner size="sm" /> : (
                          <>
                            {!f.kapak && (
                              <Star
                                size={14} role="button" title={t("firmaBilgi.kapakYap")} className="text-muted"
                                style={{ cursor: "pointer" }} onClick={() => kapakYap(f)}
                              />
                            )}
                            {/* WAS (until #615): a bare red <Pencil size={14} role="button" title={t("firmaBilgi.basligiDuzenle")} className="text-danger" /> — every other Edit button on the tab is pencil + "Edit". */}
                            <DuzenleButton
                              variant={variant} title={t("firmaBilgi.basligiDuzenle")} className="py-0 px-2"
                              onClick={() => setBaslikModal({ isOpen: true, foto: f, deger: f.baslik || "", kaydediliyor: false })}
                            />
                            <Trash2
                              size={14} role="button" title={t("firmaBilgi.kaldir")} className="text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={() => setSilModal({ isOpen: true, foto: f, siliniyor: false })}
                            />
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </FirmaBilgiCard>

      <Modal isOpen={!!onizleme} toggle={() => setOnizleme(null)} size="lg" centered>
        <ModalHeader toggle={() => setOnizleme(null)}>{onizleme?.baslik || t("firmaBilgi.firmaFotografi2")}</ModalHeader>
        <ModalBody className="text-center">
          {onizleme && (
            <img src={firmaFileSrc(onizleme.url)} alt={onizleme.baslik || ""} style={{ maxWidth: "100%" }} />
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={baslikModal.isOpen} toggle={() => setBaslikModal(BOS_BASLIK_MODAL)}>
        <ModalHeader toggle={() => setBaslikModal(BOS_BASLIK_MODAL)}>{t("firmaBilgi.fotografBasligi")}</ModalHeader>
        <ModalBody>
          <Input
            bsSize="sm" value={baslikModal.deger} placeholder={t("firmaBilgi.baslikBosBirakilabilir")}
            onChange={(e) => setBaslikModal((m) => ({ ...m, deger: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); baslikKaydet(); } }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setBaslikModal(BOS_BASLIK_MODAL)} disabled={baslikModal.kaydediliyor}>
            {t("common:iptal")}
          </Button>
          <Button color="primary" onClick={baslikKaydet} disabled={baslikModal.kaydediliyor}>
            {baslikModal.kaydediliyor ? <Spinner size="sm" /> : t("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={silModal.isOpen} toggle={() => setSilModal(BOS_SIL_MODAL)}>
        <ModalHeader toggle={() => setSilModal(BOS_SIL_MODAL)}>{t("firmaBilgi.fotografiKaldir")}</ModalHeader>
        <ModalBody className="small">
          <strong>{silModal.foto?.baslik || t("firmaBilgi.buFotograf")}</strong>{" "}{t("firmaBilgi.galeridenKaldirilacak")}
          {silModal.foto?.kapak && t("firmaBilgi.kapakFotografiydiSiradakiFotografKapak")}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setSilModal(BOS_SIL_MODAL)} disabled={silModal.siliniyor}>
            {t("common:iptal")}
          </Button>
          <Button color="danger" onClick={sil} disabled={silModal.siliniyor}>
            {silModal.siliniyor ? <Spinner size="sm" /> : t("firmaBilgi.kaldir")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ─── Konum ────────────────────────────────────────────────────────────────────

/** Adres + harita. Pin yoksa harita yerine kısa bir boş durum gösterilir. */
export function FirmaKonumCard({ firmaId, profile, variant = "header", canEdit = false, onSaved, height = 220, tour }) {
  const { t } = useTranslation("components");
  const [modalAcik, setModalAcik] = useState(false);
  const lat = profile?.latitude != null ? Number(profile.latitude) : null;
  const lng = profile?.longitude != null ? Number(profile.longitude) : null;
  const pinVar = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

  return (
    <>
      <FirmaBilgiCard
        variant={variant}
        label={t("firmaBilgi.konum")}
        actions={canEdit ? <DuzenleButton variant={variant} onClick={() => setModalAcik(true)} /> : null}
        tour={tour}
      >
        <div className="small mb-2 d-flex align-items-start gap-2">
          <MapPin size={16} className="text-danger flex-shrink-0 mt-1" />
          <span>{profile?.adres || t("firmaBilgi.adresGirilmemis")}</span>
        </div>
        {pinVar ? (
          <div className="rounded overflow-hidden border">
            <FirmaKonumHarita lat={lat} lng={lng} height={height} />
          </div>
        ) : (
          <div
            className="rounded border bg-light d-flex align-items-center justify-content-center text-muted small"
            style={{ height }}
          >
            {t("firmaBilgi.haritaKonumuIsaretlenmemis")}
          </div>
        )}
      </FirmaBilgiCard>

      {modalAcik && (
        <KonumDuzenleModal
          toggle={() => setModalAcik(false)}
          firmaId={firmaId}
          lat={pinVar ? lat : null}
          lng={pinVar ? lng : null}
          onSaved={onSaved}
        />
      )}
    </>
  );
}

// Backlog #102 (2026-09-22, Bilge'nin kararı): API anahtarı / canlı arama YOK — sadece
// yapıştırılan bir Google Maps bağlantısından enlem/boylam ayıklanır, istemci tarafında,
// tamamen client-side regex ile. Aşağıdaki dört biçim gerçek Google Maps URL'lerinde görülür:
//   .../@41.0082376,28.9783589,15z...      (harita merkezi — en sık görülen)
//   ...!3d41.0082376!4d28.9783589...       (bir "place" sonucunun kendi enlem/boylamı)
//   ...?q=41.0082,28.9784                  (eski / basit paylaşım linki)
//   ...?ll=41.0082,28.9784                 (çok eski format)
// KISALTILMIŞ linkler (goo.gl/maps/..., maps.app.goo.gl/...) bir YÖNLENDİRMEdir — hedef URL
// önce tarayıcıda açılmadan koordinat metinde yer almaz, bu yüzden burada ÇÖZÜLEMEZ; kullanıcıya
// tam adres çubuğu linkini yapıştırması gerektiği söylenir.
const GOOGLE_MAPS_COORD_PATTERNS = [
  /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/,
  /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/,
  /[?&](?:q|ll)=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/,
];
const KISALTILMIS_MAPS_LINK = /goo\.gl\/maps|maps\.app\.goo\.gl/i;

/**
 * Yapıştırılan metinden {lat,lng} ayıklar — bir Google Maps linki, ya da doğrudan
 * "41.0082, 28.9784" gibi kopyalanmış bir koordinat çifti. Hiçbiri eşleşmezse null.
 */
export function parseGoogleMapsLink(text) {
  const s = (text || "").trim();
  if (!s) return null;

  const duzCift = s.match(/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
  if (duzCift) return { lat: Number(duzCift[1]), lng: Number(duzCift[2]) };

  for (const pattern of GOOGLE_MAPS_COORD_PATTERNS) {
    const m = s.match(pattern);
    if (m) return { lat: Number(m[1]), lng: Number(m[2]) };
  }
  return null;
}

/**
 * Pin bırakarak konum seçimi. Canlı geocoding/arama yok (#61'in orijinal kararı, #102'de de
 * korundu) — haritaya tıklanır, koordinat elle yazılır, ya da bir Google Maps linki/koordinatı
 * yapıştırılıp ayıklanır. Enlem/boylam İKİSİ BİRDEN gider; ikisi de boşaltılırsa pin silinir.
 */
function KonumDuzenleModal({ toggle, firmaId, lat, lng, onSaved }) {
  const { t } = useTranslation("components");
  const [enlem, setEnlem] = useState(() => (lat != null ? String(lat) : ""));
  const [boylam, setBoylam] = useState(() => (lng != null ? String(lng) : ""));
  const [link, setLink] = useState("");
  const [linkMesaj, setLinkMesaj] = useState(null); // { color: 'success'|'danger', text }
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  function linkiCoz() {
    const sonuc = parseGoogleMapsLink(link);
    if (sonuc) {
      setEnlem(String(sonuc.lat));
      setBoylam(String(sonuc.lng));
      setHata(null);
      setLinkMesaj({ color: "success", text: t("firmaBilgi.konumBaglantidanAlindi", { lat: sonuc.lat, lng: sonuc.lng }) });
      return;
    }
    const mesaj = KISALTILMIS_MAPS_LINK.test(link)
      ? t("firmaBilgi.kisaltilmisBaglantilardanGooGlMaps")
      : t("firmaBilgi.baglantidanKonumBulunamadiGoogleMaps");
    setLinkMesaj({ color: "danger", text: mesaj });
  }

  const gecerliEnlem = enlem !== "" && Number.isFinite(Number(enlem)) ? Number(enlem) : null;
  const gecerliBoylam = boylam !== "" && Number.isFinite(Number(boylam)) ? Number(boylam) : null;

  async function kaydet() {
    const kh = koordinatHatasi(enlem, boylam);
    if (kh) { setHata(kh); return; }
    setKaydediliyor(true);
    setHata(null);
    try {
      await saveFirmaProfile(firmaId, {
        latitude: enlem === "" ? null : Number(enlem),
        longitude: boylam === "" ? null : Number(boylam),
      });
      toggle();
      onSaved?.();
    } catch (e) {
      setHata(apiErrorMessage(e, t("firmaBilgi.konumKaydedilemedi")));
    } finally {
      setKaydediliyor(false);
    }
  }

  return (
    <Modal isOpen toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{t("firmaBilgi.konumuDuzenle")}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}

        <Label className="small">{t("firmaBilgi.googleMapsBaglantisi")}</Label>
        <div className="d-flex gap-2 mb-1">
          <Input
            bsSize="sm" value={link}
            onChange={(e) => { setLink(e.target.value); setLinkMesaj(null); }}
            placeholder="https://www.google.com/maps/@41.0082,28.9784,15z…"
          />
          <Button color="secondary" outline size="sm" className="text-nowrap" disabled={!link.trim()} onClick={linkiCoz}>
            {t("firmaBilgi.konumuAl")}
          </Button>
        </div>
        {linkMesaj && (
          <div className={`small mb-2 text-${linkMesaj.color}`}>{linkMesaj.text}</div>
        )}

        <div className="text-muted small mb-2 mt-2">{t("firmaBilgi.yaDaKonumuIsaretlemekIcin")}</div>
        <div className="rounded overflow-hidden border mb-3">
          <FirmaKonumHarita
            lat={gecerliEnlem} lng={gecerliBoylam} editable height={320}
            onPick={(la, lo) => { setEnlem(String(la)); setBoylam(String(lo)); setHata(null); }}
          />
        </div>
        <Row>
          <Col md={6}>
            <Label className="small">{t("firmaBilgi.enlem")}</Label>
            <Input bsSize="sm" value={enlem} onChange={(e) => setEnlem(e.target.value)} placeholder="41.0082" />
          </Col>
          <Col md={6}>
            <Label className="small">{t("firmaBilgi.boylam")}</Label>
            <Input bsSize="sm" value={boylam} onChange={(e) => setBoylam(e.target.value)} placeholder="28.9784" />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary" outline size="sm" disabled={kaydediliyor || (enlem === "" && boylam === "")}
          onClick={() => { setEnlem(""); setBoylam(""); setHata(null); }}
        >
          {t("firmaBilgi.konumuTemizle")}
        </Button>
        <Button color="secondary" outline onClick={toggle} disabled={kaydediliyor}>{t("common:iptal")}</Button>
        <Button color="primary" onClick={kaydet} disabled={kaydediliyor}>
          {kaydediliyor ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
